import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Gift, Loader2, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, errMsg } from "@/lib/api";
import { chiaveNome } from "@/lib/format";

const norm = (s) => s.trim().replace(/\s+/g, " ");

// Searchable combobox that lets anyone create a new category on the fly ("+ Crea «X»").
export function CategoriaCombobox({ value, onChange, options, tipo, testId, placeholder = "Scegli o scrivi…", className = "", allowOmaggio = false, allowCreate = true, onCreated }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [omaggio, setOmaggio] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState([]);
  const all = useMemo(() => {
    const seen = new Set(options.map((o) => chiaveNome(o.value)));
    return [...options, ...created.filter((c) => !seen.has(chiaveNome(c.value)))];
  }, [options, created]);
  const typed = norm(q);
  const exists = typed && all.find((o) => chiaveNome(o.value) === chiaveNome(typed));
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/categorie", { tipo, nome: typed, gruppo: allowOmaggio && omaggio ? "omaggio" : null }, { params: { riusa: true } });
      const label = data.gruppo === "omaggio" ? `Omaggio › ${data.nome.replace(/^Omaggio\s+/i, "")}` : data.nome;
      setCreated((c) => [...c, { value: data.nome, label }]);
      onCreated?.(data);
      toast.success(data.esistente ? `Esiste già «${data.nome}»: uso quella` : `Categoria «${data.nome}» creata`);
      pick(data.nome);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setOmaggio(false); }
  };
  const current = all.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className={`h-10 w-full rounded-xl bg-white border border-[#EAE3DC] px-3 text-sm flex items-center justify-between gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8C6D62]/40 ${className}`}>
          <span className={`truncate ${current || value ? "" : "text-[#756B64]"}`}>{current?.label || value || placeholder}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-[#756B64]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
        <Command>
          <CommandInput data-testid={`${testId}-search`} value={q} onValueChange={setQ} placeholder="Cerca o scrivi una nuova…" />
          <CommandList>
            <CommandEmpty>{allowCreate ? "Nessuna corrispondenza" : "Nessuna categoria"}</CommandEmpty>
            <CommandGroup>
              {all.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => pick(o.value)} data-testid={`${testId}-option-${o.value.replace(/\W+/g, "-").toLowerCase()}`}>
                  <Check className={`h-3.5 w-3.5 mr-2 ${o.value === value ? "opacity-100" : "opacity-0"}`} />{o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {allowCreate && exists && exists.value !== typed && (
            <div className="border-t border-[#EAE3DC] p-2">
              <button type="button" onClick={() => pick(exists.value)} data-testid={`${testId}-usa-esistente`} className="w-full flex items-center gap-2 rounded-lg px-2 py-2 text-sm text-[#4E6656] hover:bg-[#8AA090]/15">
                <Check className="h-4 w-4" />Esiste già «{exists.label}»: usa questa
              </button>
            </div>
          )}
          {allowCreate && typed && !exists && (
            <div className="border-t border-[#EAE3DC] p-2 space-y-2">
              {allowOmaggio && (
                <label className="flex items-center gap-2 text-xs text-[#5E4A42] px-1 cursor-pointer">
                  <input type="checkbox" data-testid={`${testId}-omaggio-check`} checked={omaggio} onChange={(e) => setOmaggio(e.target.checked)} className="accent-[#8C6D62]" />
                  <Gift className="h-3 w-3" />È un tipo di omaggio (valorizzato al costo medio)
                </label>
              )}
              <button type="button" disabled={busy} onClick={create} data-testid={`${testId}-create`} className="w-full flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-semibold text-[#4E6656] hover:bg-[#8AA090]/15">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Crea «{typed}»
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const catOpts = (cats) => cats.map((c) => ({ value: c.nome, label: c.nome }));
